/**
 * TestesComportamentais - Gestão de convites para testes DISC e Big Five
 * Inclui cadastro de psicólogos e geração de relatórios PDF
 */
import React, { useState, useEffect, useCallback } from 'react';
import { testInvitesAPI, psychologistsAPI, pendingTestsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import RelatorioDISCModal from '../../components/reports/RelatorioDISCModal';
import RelatorioDISCProfissional from '../../components/reports/RelatorioDISCProfissional';
import RelatorioBigFiveModal from '../../components/reports/RelatorioBigFiveModal';
import RelatorioG2BP from '../../components/reports/RelatorioG2BP';
import RelatorioG2BPCompleto from '../../components/reports/RelatorioG2BPCompleto';
import HistoricoG2BPModal from '../../components/reports/HistoricoG2BPModal';
import { 
  Plus,
  Link2,
  Copy,
  Check,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  Loader2,
  Brain,
  Users,
  Send,
  Eye,
  RefreshCw,
  AlertTriangle,
  FileText,
  UserCog,
  Briefcase,
  Search,
  Calendar,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

// Máscara de CPF
const formatCPF = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

// Máscara de telefone
const formatPhone = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 2) return `(${numbers}`;
  if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
};

// Validação de CPF
const validateCPF = (cpf) => {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1+$/.test(digits)) return false;
  
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
  let d1 = (sum * 10 % 11) % 10;
  if (d1 !== parseInt(digits[9])) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
  let d2 = (sum * 10 % 11) % 10;
  if (d2 !== parseInt(digits[10])) return false;
  
  return true;
};

const STATUS_CONFIG = {
  pendente: { label: 'Pendente', className: 'bg-amber-100 text-amber-700', icon: Clock },
  respondido: { label: 'Respondido', className: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  expirado: { label: 'Expirado', className: 'bg-red-100 text-red-700', icon: XCircle }
};

const TIPO_TESTE_CONFIG = {
  disc: { label: 'DISC Tradicional', color: 'text-violet-600', bg: 'bg-violet-100' },
  g2bp: { label: 'G2BP', color: 'text-indigo-600', bg: 'bg-indigo-100' },
  bigfive: { label: 'Big Five', color: 'text-blue-600', bg: 'bg-blue-100' }
};

export default function TestesComportamentais() {
  const { selectedCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [filteredInvites, setFilteredInvites] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [tipoFilter, setTipoFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('convites');
  
  // Psicólogos
  const [psychologists, setPsychologists] = useState([]);
  const [loadingPsychologists, setLoadingPsychologists] = useState(false);
  const [showAddPsychologistModal, setShowAddPsychologistModal] = useState(false);
  const [newPsychologist, setNewPsychologist] = useState({ nome: '', crp: '', email: '' });
  const [addingPsychologist, setAddingPsychologist] = useState(false);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBigFiveReportModal, setShowBigFiveReportModal] = useState(false);
  const [showG2BPReportModal, setShowG2BPReportModal] = useState(false);
  const [showG2BPCompletoModal, setShowG2BPCompletoModal] = useState(false);
  const [showG2BPHistoricoModal, setShowG2BPHistoricoModal] = useState(false);
  const [g2bpHistorico, setG2bpHistorico] = useState([]);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Estado para verificação de resultado existente
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [existingResult, setExistingResult] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    tipo_teste: 'disc',
    cpf_destinatario: '',
    nome_destinatario: '',
    email_destinatario: '',
    telefone_destinatario: '',
    cargo_funcao: '',
    crp_obrigatorio: false,
    psicologo_id: ''
  });

  // Recarrega dados quando a empresa selecionada mudar
  useEffect(() => {
    setLoading(true);
    fetchInvites();
    fetchPsychologists();
  }, [selectedCompany?.company_id]);

  useEffect(() => {
    filterInvites();
  }, [invites, statusFilter, tipoFilter]);

  const fetchInvites = async () => {
    try {
      const response = await testInvitesAPI.list();
      setInvites(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar convites');
    } finally {
      setLoading(false);
    }
  };

  const fetchPsychologists = async () => {
    setLoadingPsychologists(true);
    try {
      const response = await psychologistsAPI.list();
      setPsychologists(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar psicólogos:', error);
    } finally {
      setLoadingPsychologists(false);
    }
  };

  // Buscar histórico G2BP de um candidato por CPF
  const fetchG2BPHistorico = async (cpf) => {
    if (!cpf) return;
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${API}/api/candidates/g2bp-historico/${cpf.replace(/\D/g, '')}`);
      if (response.data?.g2bp_historico) {
        setG2bpHistorico(response.data.g2bp_historico);
        setShowG2BPHistoricoModal(true);
      } else {
        toast.info('Nenhum histórico G2BP encontrado para este candidato');
      }
    } catch (error) {
      console.error('Erro ao buscar histórico G2BP:', error);
      toast.error('Erro ao buscar histórico');
    }
  };

  const filterInvites = () => {
    let filtered = [...invites];
    if (statusFilter !== 'all') {
      filtered = filtered.filter(i => i.status === statusFilter);
    }
    if (tipoFilter !== 'all') {
      filtered = filtered.filter(i => i.tipo_teste === tipoFilter);
    }
    setFilteredInvites(filtered);
  };

  // Verificar se CPF já tem resultado do teste selecionado
  const checkExistingResult = useCallback(async (cpf, tipoTeste) => {
    if (!validateCPF(cpf)) {
      setExistingResult(null);
      return;
    }
    
    setCheckingExisting(true);
    try {
      const response = await pendingTestsAPI.checkExisting(cpf.replace(/\D/g, ''), tipoTeste);
      if (response.data.has_result) {
        setExistingResult(response.data);
      } else {
        setExistingResult(null);
      }
    } catch (error) {
      console.error('Erro ao verificar resultado existente:', error);
      setExistingResult(null);
    } finally {
      setCheckingExisting(false);
    }
  }, []);

  // Debounce para verificação de CPF
  useEffect(() => {
    const timer = setTimeout(() => {
      if (form.cpf_destinatario && validateCPF(form.cpf_destinatario)) {
        checkExistingResult(form.cpf_destinatario, form.tipo_teste);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.cpf_destinatario, form.tipo_teste, checkExistingResult]);

  const handleCreate = async () => {
    if (!form.nome_destinatario.trim()) {
      toast.error('Informe o nome do destinatário');
      return;
    }
    if (!validateCPF(form.cpf_destinatario)) {
      toast.error('CPF inválido');
      return;
    }

    setCreating(true);
    try {
      const response = await testInvitesAPI.create({
        ...form,
        cpf_destinatario: form.cpf_destinatario.replace(/\D/g, ''),
        psicologo_id: (form.psicologo_id && form.psicologo_id !== 'none') ? form.psicologo_id : null
      });
      
      toast.success('Convite criado com sucesso!');
      setInvites([response.data, ...invites]);
      setShowCreateModal(false);
      resetForm();
      
      // Mostrar o link gerado
      setSelectedInvite(response.data);
      setShowLinkModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar convite');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setForm({
      tipo_teste: 'disc',
      cpf_destinatario: '',
      nome_destinatario: '',
      email_destinatario: '',
      telefone_destinatario: '',
      cargo_funcao: '',
      crp_obrigatorio: false,
      psicologo_id: ''
    });
    setExistingResult(null);
  };

  // Usar resultado existente ao invés de criar novo convite
  const handleUseExistingResult = async () => {
    if (!existingResult || !form.nome_destinatario.trim()) {
      toast.error('Informe o nome do destinatário');
      return;
    }

    setCreating(true);
    try {
      const response = await pendingTestsAPI.useExisting({
        cpf: form.cpf_destinatario.replace(/\D/g, ''),
        tipo_teste: form.tipo_teste,
        nome_destinatario: form.nome_destinatario.trim(),
        cargo_funcao: form.cargo_funcao || null,
        crp_obrigatorio: form.crp_obrigatorio,
        psicologo_id: (form.psicologo_id && form.psicologo_id !== 'none') ? form.psicologo_id : null
      });
      
      toast.success('Resultado existente vinculado com sucesso!');
      fetchInvites(); // Recarregar lista
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao usar resultado existente');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este convite?')) return;
    
    try {
      await testInvitesAPI.delete(id);
      setInvites(invites.filter(i => i.id !== id));
      toast.success('Convite excluído');
    } catch (error) {
      toast.error('Erro ao excluir convite');
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleViewResult = (invite) => {
    setSelectedInvite(invite);
    setShowResultModal(true);
  };

  const handleGenerateReport = (invite) => {
    setSelectedInvite(invite);
    if (invite.tipo_teste === 'bigfive') {
      setShowBigFiveReportModal(true);
    } else if (invite.tipo_teste === 'g2bp') {
      setShowG2BPReportModal(true);
    } else {
      setShowReportModal(true);
    }
  };

  const handleAddPsychologist = async () => {
    if (!newPsychologist.nome.trim()) {
      toast.error('Informe o nome do psicólogo');
      return;
    }
    if (!newPsychologist.crp.trim()) {
      toast.error('Informe o CRP do psicólogo');
      return;
    }

    setAddingPsychologist(true);
    try {
      const response = await psychologistsAPI.create(newPsychologist);
      setPsychologists([...psychologists, response.data]);
      setShowAddPsychologistModal(false);
      setNewPsychologist({ nome: '', crp: '', email: '' });
      toast.success('Psicólogo cadastrado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar psicólogo');
    } finally {
      setAddingPsychologist(false);
    }
  };

  const handleDeletePsychologist = async (id) => {
    if (!window.confirm('Deseja realmente remover este psicólogo?')) return;
    
    try {
      await psychologistsAPI.delete(id);
      setPsychologists(psychologists.filter(p => p.id !== id));
      toast.success('Psicólogo removido');
    } catch (error) {
      toast.error('Erro ao remover psicólogo');
    }
  };

  // Stats
  const stats = {
    total: invites.length,
    pendentes: invites.filter(i => i.status === 'pendente').length,
    respondidos: invites.filter(i => i.status === 'respondido').length,
    expirados: invites.filter(i => i.status === 'expirado').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="testes-comportamentais">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Testes Comportamentais</h1>
          <p className="text-slate-500">Envie links exclusivos para avaliações DISC e Big Five</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-violet-600 hover:bg-violet-700">
          <Plus className="w-4 h-4 mr-2" />
          Novo Convite
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <Brain className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pendentes}</p>
                <p className="text-sm text-slate-500">Pendentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.respondidos}</p>
                <p className="text-sm text-slate-500">Respondidos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.expirados}</p>
                <p className="text-sm text-slate-500">Expirados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="convites" className="gap-2">
            <Send className="w-4 h-4" />
            Convites
          </TabsTrigger>
          <TabsTrigger value="psicologos" className="gap-2">
            <UserCog className="w-4 h-4" />
            Psicólogos ({psychologists.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab: Convites */}
        <TabsContent value="convites">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Lista de Convites</CardTitle>
                <div className="flex gap-2">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="respondido">Respondido</SelectItem>
                      <SelectItem value="expirado">Expirado</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={tipoFilter} onValueChange={setTipoFilter}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="disc">DISC</SelectItem>
                      <SelectItem value="bigfive">Big Five</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" onClick={fetchInvites}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredInvites.length === 0 ? (
                <div className="text-center py-12">
                  <Brain className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhum convite encontrado</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Criar primeiro convite
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Destinatário</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Teste</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Criado em</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Expira em</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvites.map((invite) => {
                        const statusConfig = STATUS_CONFIG[invite.status];
                        const tipoConfig = TIPO_TESTE_CONFIG[invite.tipo_teste];
                        const StatusIcon = statusConfig.icon;
                        
                        return (
                          <tr key={invite.id} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium">{invite.nome_destinatario}</p>
                                <p className="text-sm text-slate-500">
                                  {formatCPF(invite.cpf_destinatario)}
                                </p>
                                {invite.cargo_funcao && (
                                  <p className="text-xs text-slate-400">{invite.cargo_funcao}</p>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={`${tipoConfig.bg} ${tipoConfig.color}`}>
                                {tipoConfig.label}
                              </Badge>
                              {invite.crp_obrigatorio && (
                                <Badge variant="outline" className="ml-1 text-xs">CRP</Badge>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={statusConfig.className}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600">
                              {new Date(invite.created_at).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600">
                              {new Date(invite.expires_at).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex justify-end gap-1">
                                {invite.status === 'pendente' && invite.link && (
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => handleCopyLink(invite.link)}
                                    title="Copiar link"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                )}
                                {invite.status === 'respondido' && (
                                  <>
                                    <Button 
                                      variant="ghost" 
                                      size="icon"
                                      onClick={() => handleViewResult(invite)}
                                      title="Ver resultado"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    {invite.tipo_teste === 'disc' && invite.resultado?.disc_profile && (
                                      <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => handleGenerateReport(invite)}
                                        title="Gerar relatório PDF"
                                      >
                                        <FileText className="w-4 h-4 text-violet-600" />
                                      </Button>
                                    )}
                                    {invite.tipo_teste === 'bigfive' && invite.resultado?.bigfive_result && (
                                      <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => handleGenerateReport(invite)}
                                        title="Gerar relatório PDF"
                                      >
                                        <FileText className="w-4 h-4 text-blue-600" />
                                      </Button>
                                    )}
                                    {invite.tipo_teste === 'g2bp' && invite.resultado?.g2bp_result && (
                                      <div className="flex gap-1">
                                        <Button 
                                          variant="ghost" 
                                          size="icon"
                                          onClick={() => {
                                            setSelectedInvite(invite);
                                            setShowG2BPReportModal(true);
                                          }}
                                          title="Relatório Simplificado (3 páginas)"
                                        >
                                          <FileText className="w-4 h-4 text-indigo-600" />
                                        </Button>
                                        <Button 
                                          variant="ghost" 
                                          size="icon"
                                          onClick={() => {
                                            setSelectedInvite(invite);
                                            setShowG2BPCompletoModal(true);
                                          }}
                                          title="Relatório Completo (30 páginas)"
                                          className="relative"
                                        >
                                          <FileText className="w-4 h-4 text-violet-600" />
                                          <span className="absolute -top-1 -right-1 w-3 h-3 bg-violet-600 rounded-full text-[8px] text-white flex items-center justify-center">30</span>
                                        </Button>
                                        <Button 
                                          variant="ghost" 
                                          size="icon"
                                          onClick={() => {
                                            setSelectedInvite(invite);
                                            fetchG2BPHistorico(invite.candidate_cpf);
                                          }}
                                          title="Ver Histórico de Avaliações"
                                        >
                                          <Clock className="w-4 h-4 text-slate-500" />
                                        </Button>
                                      </div>
                                    )}
                                  </>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => handleDelete(invite.id)}
                                  className="text-red-600 hover:text-red-700"
                                  title="Excluir"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Psicólogos */}
        <TabsContent value="psicologos">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Psicólogos Cadastrados</CardTitle>
                  <CardDescription>
                    Cadastre psicólogos para assinatura de relatórios
                  </CardDescription>
                </div>
                <Button onClick={() => setShowAddPsychologistModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Cadastrar Psicólogo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingPsychologists ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : psychologists.length === 0 ? (
                <div className="text-center py-12">
                  <UserCog className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-2">Nenhum psicólogo cadastrado</p>
                  <p className="text-sm text-slate-400">
                    Cadastre psicólogos para poderem assinar relatórios de avaliação
                  </p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {psychologists.map((psi) => (
                    <div 
                      key={psi.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center">
                          <UserCog className="w-6 h-6 text-violet-600" />
                        </div>
                        <div>
                          <p className="font-semibold">{psi.nome}</p>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span>CRP: {psi.crp}</span>
                            {psi.email && <span>{psi.email}</span>}
                          </div>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDeletePsychologist(psi.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-violet-600" />
              Novo Convite de Teste
            </DialogTitle>
            <DialogDescription>
              Envie um link exclusivo para avaliação comportamental
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Teste *</Label>
              <Select 
                value={form.tipo_teste} 
                onValueChange={(v) => setForm({...form, tipo_teste: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="disc">DISC Tradicional</SelectItem>
                  <SelectItem value="g2bp">G2BP (Completo)</SelectItem>
                  <SelectItem value="bigfive">Big Five - Personalidade</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome Completo *</Label>
                <Input
                  value={form.nome_destinatario}
                  onChange={(e) => setForm({...form, nome_destinatario: e.target.value})}
                  placeholder="Nome do destinatário"
                />
              </div>
              <div className="space-y-2">
                <Label>CPF *</Label>
                <div className="relative">
                  <Input
                    value={form.cpf_destinatario}
                    onChange={(e) => setForm({...form, cpf_destinatario: formatCPF(e.target.value)})}
                    placeholder="000.000.000-00"
                    maxLength={14}
                  />
                  {checkingExisting && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Alerta de resultado existente */}
            {existingResult && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-emerald-900">
                      Este CPF já possui resultado de {form.tipo_teste.toUpperCase()}!
                    </h4>
                    <p className="text-sm text-emerald-700 mt-1">
                      <strong>{existingResult.candidate_name}</strong> já respondeu este teste
                      {existingResult.age_months !== null && (
                        <span className="ml-1">
                          há {existingResult.age_months === 0 ? 'menos de 1 mês' : 
                              existingResult.age_months === 1 ? '1 mês' : 
                              `${existingResult.age_months} meses`}
                        </span>
                      )}
                      .
                    </p>
                    
                    {existingResult.age_months > 6 && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-amber-700 bg-amber-50 p-2 rounded">
                        <AlertTriangle className="w-3 h-3" />
                        <span>Resultado com mais de 6 meses. Considere solicitar nova avaliação.</span>
                      </div>
                    )}
                    
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        onClick={handleUseExistingResult}
                        disabled={creating || !form.nome_destinatario.trim()}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                        Usar resultado existente
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setExistingResult(null)}
                      >
                        Solicitar novo teste
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Cargo / Função</Label>
              <Input
                value={form.cargo_funcao}
                onChange={(e) => setForm({...form, cargo_funcao: e.target.value})}
                placeholder="Ex: Analista de RH"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={form.email_destinatario}
                  onChange={(e) => setForm({...form, email_destinatario: e.target.value})}
                  placeholder="email@exemplo.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={form.telefone_destinatario}
                  onChange={(e) => setForm({...form, telefone_destinatario: formatPhone(e.target.value)})}
                  placeholder="(00) 00000-0000"
                  maxLength={16}
                />
              </div>
            </div>

            {/* Psicólogo e CRP */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <h4 className="font-medium text-slate-700 flex items-center gap-2">
                <UserCog className="w-4 h-4" />
                Configurações de Relatório
              </h4>
              
              {psychologists.length > 0 && (
                <div className="space-y-2">
                  <Label>Psicólogo Responsável</Label>
                  <Select 
                    value={form.psicologo_id} 
                    onValueChange={(v) => setForm({...form, psicologo_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {psychologists.map((psi) => (
                        <SelectItem key={psi.id} value={psi.id}>
                          {psi.nome} - CRP: {psi.crp}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="crp_obrigatorio"
                  checked={form.crp_obrigatorio}
                  onCheckedChange={(checked) => setForm({...form, crp_obrigatorio: checked})}
                />
                <Label htmlFor="crp_obrigatorio" className="text-sm cursor-pointer">
                  Exigir CRP na geração do relatório
                </Label>
              </div>
              
              {form.crp_obrigatorio && (
                <div className="flex items-start gap-2 p-2 bg-amber-50 rounded border border-amber-200">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Relatórios só poderão ser gerados com um psicólogo cadastrado (Nome + CRP)
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={creating}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Criando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Criar Convite
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-emerald-600" />
              Link do Convite
            </DialogTitle>
          </DialogHeader>

          {selectedInvite && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                Compartilhe este link com <strong>{selectedInvite.nome_destinatario}</strong> para que responda o teste {TIPO_TESTE_CONFIG[selectedInvite.tipo_teste].label}.
              </p>
              
              <div className="flex gap-2">
                <Input 
                  value={selectedInvite.link} 
                  readOnly 
                  className="bg-slate-50"
                />
                <Button onClick={() => handleCopyLink(selectedInvite.link)}>
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  O link expira em 7 dias ({new Date(selectedInvite.expires_at).toLocaleDateString('pt-BR')})
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowLinkModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Modal */}
      <Dialog open={showResultModal} onOpenChange={setShowResultModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Resultado do Teste
            </DialogTitle>
          </DialogHeader>

          {selectedInvite && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Nome</Label>
                  <p className="font-medium">{selectedInvite.nome_destinatario}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Teste</Label>
                  <Badge className={TIPO_TESTE_CONFIG[selectedInvite.tipo_teste].bg + ' ' + TIPO_TESTE_CONFIG[selectedInvite.tipo_teste].color}>
                    {TIPO_TESTE_CONFIG[selectedInvite.tipo_teste].label}
                  </Badge>
                </div>
                {selectedInvite.cargo_funcao && (
                  <div>
                    <Label className="text-xs text-slate-500">Cargo</Label>
                    <p className="font-medium">{selectedInvite.cargo_funcao}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-500">Respondido em</Label>
                  <p className="font-medium">
                    {selectedInvite.responded_at 
                      ? new Date(selectedInvite.responded_at).toLocaleString('pt-BR')
                      : '-'
                    }
                  </p>
                </div>
              </div>

              {selectedInvite.tipo_teste === 'disc' && selectedInvite.resultado?.disc_profile && (
                <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                  <Label className="text-sm text-violet-700">Perfil DISC</Label>
                  <p className="text-2xl font-bold text-violet-900 mt-1">
                    {selectedInvite.resultado.disc_profile}
                  </p>
                  {selectedInvite.resultado.disc_detalhado?.texto_explicativo && (
                    <p className="text-sm text-violet-700 mt-2">
                      {selectedInvite.resultado.disc_detalhado.texto_explicativo.slice(0, 200)}...
                    </p>
                  )}
                </div>
              )}

              {selectedInvite.tipo_teste === 'bigfive' && selectedInvite.resultado?.bigfive_result && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-sm text-blue-700">Perfil Big Five</Label>
                  <p className="text-lg font-bold text-blue-900 mt-1">
                    Dominante: {selectedInvite.resultado.bigfive_result.dominant}
                  </p>
                </div>
              )}

              {selectedInvite.tipo_teste === 'g2bp' && selectedInvite.resultado?.g2bp_result && (
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200 space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <Label className="text-sm text-indigo-700">G2 Behavioral Profile</Label>
                      <p className="text-lg font-bold text-indigo-900 mt-1">
                        Perfil: {selectedInvite.resultado.g2bp_result.estilo || selectedInvite.resultado.g2bp_result.dominant}
                      </p>
                      {selectedInvite.resultado.g2bp_result.versao && (
                        <Badge variant="outline" className="text-xs mt-1">
                          Algoritmo v{selectedInvite.resultado.g2bp_result.versao}
                        </Badge>
                      )}
                    </div>
                    {selectedInvite.resultado.g2bp_result.inconsistente && (
                      <Badge className="bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Sob Análise
                      </Badge>
                    )}
                  </div>
                  
                  {/* Gráfico Síntese */}
                  {(selectedInvite.resultado.g2bp_result.percentuaisSintese || selectedInvite.resultado.g2bp_result.percentages) && (
                    <div>
                      <Label className="text-xs text-indigo-600 mb-2 block">Perfil Composto (Síntese)</Label>
                      <div className="flex gap-2">
                        {['D', 'I', 'S', 'C'].map(trait => {
                          const perc = (selectedInvite.resultado.g2bp_result.percentuaisSintese || selectedInvite.resultado.g2bp_result.percentages)[trait];
                          const colors = {
                            D: 'bg-red-500',
                            I: 'bg-amber-500',
                            S: 'bg-green-500',
                            C: 'bg-blue-500'
                          };
                          return (
                            <div key={trait} className="flex-1 text-center">
                              <div className="h-16 bg-slate-200 rounded relative overflow-hidden">
                                <div 
                                  className={`absolute bottom-0 w-full ${colors[trait]} transition-all`}
                                  style={{ height: `${perc}%` }}
                                />
                              </div>
                              <span className="text-xs font-bold mt-1 block">{trait}</span>
                              <span className="text-xs text-slate-600">{perc}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Gráficos MAIS e MENOS se disponíveis */}
                  {selectedInvite.resultado.g2bp_result.percentuaisMais && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-green-600 mb-1 block">Perfil Público (MAIS)</Label>
                        <div className="flex gap-1">
                          {['D', 'I', 'S', 'C'].map(trait => (
                            <div key={trait} className="flex-1 text-center">
                              <div className="h-8 bg-green-100 rounded relative overflow-hidden">
                                <div 
                                  className="absolute bottom-0 w-full bg-green-500"
                                  style={{ height: `${selectedInvite.resultado.g2bp_result.percentuaisMais[trait]}%` }}
                                />
                              </div>
                              <span className="text-[10px]">{trait}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-red-600 mb-1 block">Perfil Natural (MENOS)</Label>
                        <div className="flex gap-1">
                          {['D', 'I', 'S', 'C'].map(trait => (
                            <div key={trait} className="flex-1 text-center">
                              <div className="h-8 bg-red-100 rounded relative overflow-hidden">
                                <div 
                                  className="absolute bottom-0 w-full bg-red-500"
                                  style={{ height: `${selectedInvite.resultado.g2bp_result.percentuaisMenos[trait]}%` }}
                                />
                              </div>
                              <span className="text-[10px]">{trait}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {((selectedInvite.tipo_teste === 'disc' && !selectedInvite.resultado?.disc_profile) || 
                (selectedInvite.tipo_teste === 'bigfive' && !selectedInvite.resultado?.bigfive_result) ||
                (selectedInvite.tipo_teste === 'g2bp' && !selectedInvite.resultado?.g2bp_result)) && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Resultado não disponível</p>
                      <p className="text-sm text-amber-700 mt-1">
                        O candidato ainda não completou o teste ou o resultado ainda não foi sincronizado com a plataforma.
                      </p>
                      <p className="text-xs text-amber-600 mt-2">
                        Peça ao candidato para acessar o App Móvel e verificar se o teste foi finalizado corretamente.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {selectedInvite?.tipo_teste === 'disc' && selectedInvite?.resultado?.disc_profile && (
              <Button 
                variant="outline"
                onClick={() => {
                  setShowResultModal(false);
                  handleGenerateReport(selectedInvite);
                }}
              >
                <FileText className="w-4 h-4 mr-2" />
                Gerar Relatório
              </Button>
            )}
            {selectedInvite?.tipo_teste === 'bigfive' && selectedInvite?.resultado?.bigfive_result && (
              <Button 
                variant="outline"
                onClick={() => {
                  setShowResultModal(false);
                  handleGenerateReport(selectedInvite);
                }}
              >
                <FileText className="w-4 h-4 mr-2" />
                Gerar Relatório
              </Button>
            )}
            {selectedInvite?.tipo_teste === 'g2bp' && selectedInvite?.resultado?.g2bp_result && (
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowResultModal(false);
                    setShowG2BPReportModal(true);
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Simplificado
                </Button>
                <Button 
                  variant="default"
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={() => {
                    setShowResultModal(false);
                    setShowG2BPCompletoModal(true);
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Completo (30 pág.)
                </Button>
              </div>
            )}
            <Button onClick={() => setShowResultModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Psychologist Modal */}
      <Dialog open={showAddPsychologistModal} onOpenChange={setShowAddPsychologistModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-violet-600" />
              Cadastrar Psicólogo
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome Completo *</Label>
              <Input
                value={newPsychologist.nome}
                onChange={(e) => setNewPsychologist({...newPsychologist, nome: e.target.value})}
                placeholder="Nome do psicólogo"
              />
            </div>
            <div className="space-y-2">
              <Label>CRP *</Label>
              <Input
                value={newPsychologist.crp}
                onChange={(e) => setNewPsychologist({...newPsychologist, crp: e.target.value.toUpperCase()})}
                placeholder="Ex: 06/123456"
              />
            </div>
            <div className="space-y-2">
              <Label>E-mail</Label>
              <Input
                type="email"
                value={newPsychologist.email}
                onChange={(e) => setNewPsychologist({...newPsychologist, email: e.target.value})}
                placeholder="email@exemplo.com"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPsychologistModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAddPsychologist}
              disabled={addingPsychologist}
            >
              {addingPsychologist ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Cadastrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Modal - DISC Profissional */}
      <RelatorioDISCProfissional
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        invite={selectedInvite}
        psychologists={psychologists}
        crpObrigatorio={selectedInvite?.crp_obrigatorio}
      />

      {/* Big Five Report Modal */}
      <RelatorioBigFiveModal
        open={showBigFiveReportModal}
        onClose={() => setShowBigFiveReportModal(false)}
        invite={selectedInvite}
        psychologists={psychologists}
        crpObrigatorio={selectedInvite?.crp_obrigatorio}
      />

      {/* G2BP Report Modal */}
      <RelatorioG2BP
        open={showG2BPReportModal}
        onClose={() => setShowG2BPReportModal(false)}
        invite={selectedInvite}
        psychologists={psychologists}
        crpObrigatorio={selectedInvite?.crp_obrigatorio}
      />

      {/* G2BP Report Completo Modal (30 páginas) */}
      <RelatorioG2BPCompleto
        open={showG2BPCompletoModal}
        onClose={() => setShowG2BPCompletoModal(false)}
        invite={selectedInvite}
        psychologists={psychologists}
        crpObrigatorio={selectedInvite?.crp_obrigatorio}
      />

      {/* Histórico G2BP Modal */}
      <HistoricoG2BPModal
        open={showG2BPHistoricoModal}
        onClose={() => setShowG2BPHistoricoModal(false)}
        historico={g2bpHistorico}
        nomeCandidata={selectedInvite?.nome_destinatario || ''}
      />
    </div>
  );
}
